import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import RoutePageFallback from './RoutePageFallback';

const Profile = lazy(() => import('../profile.component'));
const BoardUser = lazy(() => import('../board-user.component'));
const BoardModerator = lazy(() => import('../board-moderator.component'));
const BoardAdmin = lazy(() => import('../board-admin.component'));
const CaseCreate = lazy(() => import('../case/create_case.component'));
const MyCase = lazy(() => import('../case/mycase.component'));
const MyTask = lazy(() => import('../case/my_task.component'));
const Company = lazy(() => import('../administrator/company.component'));
const Case = lazy(() => import('../case/case.component'));
const ValidateCase = lazy(() => import('../case/validate_case.component'));
const ProductOwnerReport = lazy(() => import('../report/product-owner-report.component'));
const Plant = lazy(() => import('../administrator/plant.component'));
const Product = lazy(() => import('../administrator/product.component'));
const Role = lazy(() => import('../administrator/role.component'));
const User = lazy(() => import('../administrator/user.component'));
const IssueType = lazy(() => import('../administrator/issue_type.component'));
const IssueCategory = lazy(() => import('../administrator/issue_category.component'));
const SendEmail = lazy(() => import('../administrator/send_email.component'));
const TaskHistory = lazy(() => import('../task/TaskHistory'));
const BulkUpdateMember = lazy(() => import('../administrator/bulk_update_member.component'));
const ServiceRequest = lazy(() => import('../case/service_request.component'));
const ProductGroup = lazy(() => import('../administrator/product_group.component'));
const MyServiceRequest = lazy(() => import('../case/myservice_request.component'));
const AllServiceRequest = lazy(() => import('../case/all_service_request.component'));
const ServiceRequestIntegration = lazy(() => import('../service_request_integration.component'));
const ApprovalEffortReview = lazy(() => import('../service/approval-effort-review.component'));
const MyTaskServiceRequest = lazy(() => import('../case/mytask_service_request.component'));
const RequesterReviewService = lazy(() => import('../case/requester-review.component'));
const FinalReviewService = lazy(() => import('../case/final-review-service.component'));
const InternalReviewService = lazy(() => import('../case/internal-review.component'));
const AcceptService = lazy(() => import('../case/accept_service.component'));
const ViewService = lazy(() => import('../case/view-service.component'));
const PerformService = lazy(() => import('../case/perform_service.component'));
const PICActualService = lazy(() => import('../case/pic_actual_service.component'));
const EstimateEffort = lazy(() => import('../case/estimate_effort.component'));
const ReviewerEffort = lazy(() => import('../case/reviewer_effort.component'));
const ReleaseNote = lazy(() => import('../release-note.component'));
const ViewMyCasePage = lazy(() => import('../case/view_case.component'));
const ViewMyTaskPage = lazy(() => import('../case/view_mytask.component'));
const ViewCaseDetailPage = lazy(() => import('../case/view_case_detail.component'));
const Special = lazy(() => import('../administrator/special.component'));
const Dashboard = lazy(() => import('../administrator/dashboard.component'));
const CustomerDashboard = lazy(() => import('../customer-company/customer-dashboard.component'));
const CustomerCases = lazy(() => import('../customer-company/customer-cases.component'));
const CustomerViewCase = lazy(() => import('../customer-company/customer-view-case.component'));
const SalesCases = lazy(() => import('../sales/sales-cases.component'));
const CompleteCase = lazy(() => import('../administrator/complete-case.component'));
const FollowUpTask = lazy(() => import('../administrator/follow-up-task.component'));
const ServiceDeskCreateCase = lazy(() => import('../administrator/service_desk_create_case.component'));
const PriorityManagement = lazy(() => import('../priority/priority.component'));
const HolidayManagement = lazy(() => import('../holiday/holiday.component'));
const SlaConfiguration = lazy(() => import('../sla/sla-configuration.component'));
const SlaBreachWatchlist = lazy(() => import('../sla/sla-breach-watchlist.component'));
const ExecutiveReport = lazy(() => import('../administrator/executive-report.component'));
const ProductRoadmap = lazy(() => import('../administrator/product_roadmap.component'));
const DirectApproval = lazy(() => import('../case/direct_approval.component'));
const ServiceType = lazy(() => import('../administrator/service_type.component'));
const ServiceCategory = lazy(() => import('../administrator/service_category.component'));
const JobRole = lazy(() => import('../administrator/job_role.component'));
const ServiceApproval = lazy(() => import('../administrator/service_approval/service_approval.component'));
const FinalReviewer = lazy(() => import('../administrator/service_approval/final_reviewer.component'));
const EstimateEffortReviewer = lazy(() => import('../administrator/service_approval/estimate_effort_reviewer.component'));
const FinalApproval = lazy(() => import('../administrator/service_approval/final_approval.component'));
const LegalResource = lazy(() => import('../legal-resource.component'));
const EscalationSettings = lazy(() => import('../administrator/escalation-settings.component'));
const EscalationDashboard = lazy(() => import('../administrator/escalation-dashboard.component'));
const EscalationPublicCaseView = lazy(() => import('../escalation/EscalationPublicCaseView'));
const EscalationPublicView = lazy(() => import('../escalation/EscalationPublicView'));

function DirectApprovalWrapper(props) {
    const params = useParams();
    return <DirectApproval {...props} params={params} />;
}

/**
 * Logged-in application routes — each page is loaded on demand (code splitting).
 */
export default function AuthenticatedRoutes({ serviceRequestCallback }) {
    return (
        <Suspense fallback={<RoutePageFallback />}>
            <Routes>
                {/* ล็อกอินแล้วเปิด /login (เช่น automation) — ไม่มี route เดิม ทำให้ URL กับ UI ไม่ตรงกัน */}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/user" element={<BoardUser />} />
                <Route path="/mod" element={<BoardModerator />} />
                <Route path="/admin" element={<BoardAdmin />} />
                <Route path="/case" element={<Case />} />
                <Route path="/mytask" element={<MyTask />} />
                <Route path="/task/:task_id" element={<TaskHistory />} />
                <Route path="/" element={<MyCase />} />
                <Route path="/product_owner/validate_case" element={<ValidateCase />} />
                <Route path="/product_owner/report" element={<ProductOwnerReport />} />
                <Route path="/view-mycase/:id" element={<ViewMyCasePage />} />
                <Route path="/view-mytask/:id" element={<ViewMyTaskPage />} />
                <Route path="/view-case/:id" element={<ViewCaseDetailPage />} />
                <Route path="/case/create" element={<CaseCreate />} />
                <Route path="/administrator/company" element={<Company />} />
                <Route path="/administrator/plant" element={<Plant />} />
                <Route path="/administrator/product_group" element={<ProductGroup />} />
                <Route path="/administrator/product" element={<Product />} />
                <Route path="/administrator/service-type" element={<ServiceType />} />
                <Route path="/administrator/service-category" element={<ServiceCategory />} />
                <Route path="/administrator/job-role" element={<JobRole />} />
                <Route path="/administrator/service-approval" element={<ServiceApproval />} />
                <Route path="/administrator/final-reviewer" element={<FinalReviewer />} />
                <Route path="/administrator/estimate-reviewer" element={<EstimateEffortReviewer />} />
                <Route path="/administrator/final-approval" element={<FinalApproval />} />
                <Route path="/administrator/role" element={<Role />} />
                <Route path="/administrator/user" element={<User />} />
                <Route path="/administrator/issue_type" element={<IssueType />} />
                <Route path="/administrator/issue_category" element={<IssueCategory />} />
                <Route path="/administrator/send_email" element={<SendEmail />} />
                <Route path="/administrator/bulk_update_member" element={<BulkUpdateMember />} />
                <Route path="/administrator/special" element={<Special />} />
                <Route path="/administrator/dashboard" element={<Dashboard />} />
                <Route path="/administrator/complete-case" element={<CompleteCase />} />
                <Route path="/administrator/follow-up-task" element={<FollowUpTask />} />
                <Route path="/administrator/service-desk-create-case" element={<ServiceDeskCreateCase />} />
                <Route path="/administrator/priority" element={<PriorityManagement />} />
                <Route path="/administrator/holiday" element={<HolidayManagement />} />
                <Route path="/administrator/sla-configuration" element={<SlaConfiguration />} />
                <Route path="/administrator/sla-breach-watchlist" element={<SlaBreachWatchlist />} />
                <Route path="/administrator/executive-report" element={<ExecutiveReport />} />
                <Route path="/administrator/product-roadmap" element={<ProductRoadmap />} />
                <Route path="/service_request/create" element={<ServiceRequest />} />
                <Route path="/service_request/my-service-request" element={<MyServiceRequest />} />
                <Route path="/service_request/all_service_request" element={<AllServiceRequest />} />
                <Route path="/service_request_integration" element={<ServiceRequestIntegration valueCallback={serviceRequestCallback} />} />
                <Route path="/service_request/my-task-service-request" element={<MyTaskServiceRequest />} />
                <Route path="/service_request/estimate-effort/:id" element={<EstimateEffort />} />
                <Route path="/service_request/review-effort/:id" element={<ReviewerEffort />} />
                <Route path="/service_request/approval_effort_review/:id" element={<ApprovalEffortReview />} />
                <Route path="/service_request/perform-service/:id" element={<PerformService />} />
                <Route path="/service_request/pic-actual-service/:id" element={<PICActualService />} />
                <Route path="/service_request/internal-review-service/:id" element={<InternalReviewService />} />
                <Route path="/service_request/requester-review-service/:id" element={<RequesterReviewService />} />
                <Route path="/service_request/final-review-service/:id" element={<FinalReviewService />} />
                <Route path="/service_request/accept-service/:id" element={<AcceptService />} />
                <Route path="/service_request/view-service/:id" element={<ViewService />} />
                <Route path="/release-note" element={<ReleaseNote />} />
                <Route path="/customer-report/dashboard" element={<CustomerDashboard />} />
                <Route path="/customer-report/cases" element={<CustomerCases />} />
                <Route path="/customer-report/case/:id" element={<CustomerViewCase />} />
                <Route path="/sales/cases" element={<SalesCases />} />
                <Route path="/sales/case/:id" element={<CustomerViewCase />} />
                <Route path="/direct-approval/:id" element={<DirectApprovalWrapper />} />
                <Route path="/legal-resource" element={<LegalResource />} />
                <Route path="/administrator/escalation-settings" element={<EscalationSettings />} />
                <Route path="/administrator/escalation" element={<EscalationDashboard />} />
                {/* Public escalation links — accessible whether logged in or not */}
                <Route path="/public/escalation/:token/case/:caseId" element={<EscalationPublicCaseView />} />
                <Route path="/public/escalation/:token" element={<EscalationPublicView />} />
            </Routes>
        </Suspense>
    );
}
